import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AnalyticsContext, trackEvent } from 'latitude-analytics';
import { debounce as _debounce } from 'lodash';
import styled from 'styled-components';
import SvgInline from '@latitude/svg-inline';
import { Text } from '@latitude/text';
import RadioBoxes from '../RadioBoxes/RadioBoxesV2/RadioBoxes';
import { Box } from '../Box/Box';
import {
  BREAKPOINT,
  LOAN_PAY_FREQUENCY,
  PL_MINIMUM_AMOUNT,
  PL_MINIMUM_LOAN_TERM,
  PL_MAXIMUM_LOAN_TERM,
  PL_MONTHLY_FEE,
  PL_PPSR_SEARCH_FEE,
  PL_PPSR_REGISTRATION_FEE,
  PL_ESTABLISHMENT_FEE,
  CALCULATOR_TRACKING_V2
} from '../../utils/constants';
import { getRepaymentFigures } from '../../utils/loanCalculatorUtil';
import Modal from '../ModalRebrand/ModalRebrand';
import {
  directTrackLink,
  normaliseForAnalytics
} from '../../utils/analyticsUtil';
import {
  customLinkNames,
  getCustomLinkEventName
} from './PersonalLoanCalculator.analytics';
import { List, ListItem } from '@latitude/list';
import { ALIGN, REBRAND_COLOR } from '@latitude/core/utils/constants';
import Typography from '@latitude/typography';
import Button from '@latitude/button-rebrand';
import { getRateBounds } from './rateConfig';
import * as Yup from 'yup';
import { Formik, Form, ErrorMessage } from 'formik';
import Select from 'react-select';

const PL_MAXIMUM_AMOUNT = 200000;
const INITIAL_LOAN_AMOUNT = 20000;
const INITIAL_INTEREST_RATE = 16;
const INITIAL_LOAN_TERM = 7;
const SESSION_STORAGE_KEY = 'personal-loan-calculator-values';

function CalculatorFields({
  salesMode,
  setRepaymentAmount,
  setTotalInterestPaid,
  setTotalAmountPaid,
  repaymentFreq,
  setRepaymentFreq,
  isVariable,
  setIsVariable,
  extraRepayments,
  setInterestSaved,
  setTimeSavedMonths,
  setTimeSavedYears,
  loans1,
  isLefWaiver,
  isFinder,
  setValidationError,
  validationError,
  isSecured,
  setIsSecured
}) {
  const [loanAmount, setLoanAmount] = useState(INITIAL_LOAN_AMOUNT);
  const [hasError, setHasError] = useState(false);
  const [repaymentYears, setRepaymentYears] = useState(INITIAL_LOAN_TERM);
  const [showModal, setShowModal] = useState(false);
  const [showModalSecured, setShowModalSecured] = useState(false);
  const [interestRate, setInterestRate] = useState(INITIAL_INTEREST_RATE);
  const [termFocused, setTermFocused] = useState(false);
  const [rateFocused, setRateFocused] = useState(false);

  const SECURED_MIN_RATE = 8.99;
  const SECURED_MAX_RATE = 28.39;
  const SECURED_MIN_COMP_RATE = isLefWaiver ? 10.11 : 10.67;
  const SECURED_MAX_COMP_RATE = isLefWaiver ? 29.29 : 29.97;

  const {
    minInterestRate,
    maxInterestRate,
    minComparisonRate,
    maxComparisonRate
  } = getRateBounds({
    loans1,
    isVariable,
    isFinder,
    isLefWaiver
  });
  const [analytics] = useContext(AnalyticsContext);

  const validationSchema = Yup.object({
    amount: Yup.number()
      .typeError(
        `Your total loan amount must be between $${PL_MINIMUM_AMOUNT.toLocaleString()} and $${PL_MAXIMUM_AMOUNT.toLocaleString()}`
      )
      .min(
        PL_MINIMUM_AMOUNT,
        `Your total loan amount must be between $${PL_MINIMUM_AMOUNT.toLocaleString()} and $${PL_MAXIMUM_AMOUNT.toLocaleString()}`
      )
      .max(
        PL_MAXIMUM_AMOUNT,
        `Your total loan amount must be between $${PL_MINIMUM_AMOUNT.toLocaleString()} and $${PL_MAXIMUM_AMOUNT.toLocaleString()}`
      )
      .required(
        `Your total loan amount must be between $${PL_MINIMUM_AMOUNT.toLocaleString()} and $${PL_MAXIMUM_AMOUNT.toLocaleString()}`
      ),

    term: Yup.number()
      .typeError(
        `Your loan term must be from ${PL_MINIMUM_LOAN_TERM} to ${PL_MAXIMUM_LOAN_TERM} years`
      )
      .min(
        PL_MINIMUM_LOAN_TERM,
        `Your loan term must be from ${PL_MINIMUM_LOAN_TERM} to ${PL_MAXIMUM_LOAN_TERM} years`
      )
      .max(
        PL_MAXIMUM_LOAN_TERM,
        `Your loan term must be from ${PL_MINIMUM_LOAN_TERM} to ${PL_MAXIMUM_LOAN_TERM} years`
      )
      .required(
        `Your loan term must be from ${PL_MINIMUM_LOAN_TERM} to ${PL_MAXIMUM_LOAN_TERM} years`
      ),

    rate: Yup.number()
      .typeError(
        `Enter a rate within the range for our ${
          isSecured ? 'fixed' : 'variable'
        } ${isSecured ? 'secured' : 'unsecured'} Personal Loan (${
          isSecured ? SECURED_MIN_RATE : minInterestRate.toFixed(2)
        }% p.a. - ${
          isSecured ? SECURED_MAX_RATE : maxInterestRate.toFixed(2)
        }% p.a.)`
      )
      .min(
        isSecured ? SECURED_MIN_RATE : minInterestRate,
        `Enter a rate within the range for our ${
          isSecured ? 'fixed' : 'variable'
        } ${isSecured ? 'secured' : 'unsecured'} Personal Loan (${
          isSecured ? SECURED_MIN_RATE : minInterestRate.toFixed(2)
        }% p.a. - ${
          isSecured ? SECURED_MAX_RATE : maxInterestRate.toFixed(2)
        }% p.a.)`
      )
      .max(
        isSecured ? SECURED_MAX_RATE : maxInterestRate,
        `Enter a rate within the range for our ${
          isSecured ? 'fixed' : 'variable'
        } ${isSecured ? 'secured' : 'unsecured'} Personal Loan (${
          isSecured ? SECURED_MIN_RATE : minInterestRate.toFixed(2)
        }% p.a. - ${
          isSecured ? SECURED_MAX_RATE : maxInterestRate.toFixed(2)
        }% p.a.)`
      )
      .required(
        `Enter a rate within the range for our ${
          isSecured ? 'fixed' : 'variable'
        } ${isSecured ? 'secured' : 'unsecured'} Personal Loan (${
          isSecured ? SECURED_MIN_RATE : minInterestRate.toFixed(2)
        }% p.a. - ${
          isSecured ? SECURED_MAX_RATE : maxInterestRate.toFixed(2)
        }% p.a.)`
      )
  });

  const rateFixed = `Enter a rate within the range for our fixed ${
    isSecured ? 'secured' : 'unsecured'
  } Personal Loan (${
    isSecured ? SECURED_MIN_RATE : minInterestRate.toFixed(2)
  }% p.a. - ${
    isSecured ? SECURED_MAX_RATE : maxInterestRate.toFixed(2)
  }% p.a.)`;

  const rateVariable = `Enter a rate within the range for our variable ${
    isSecured ? 'secured' : 'unsecured'
  } Personal Loan (${
    isSecured ? SECURED_MIN_RATE : minInterestRate.toFixed(2)
  }% p.a. - ${
    isSecured ? SECURED_MAX_RATE : maxInterestRate.toFixed(2)
  }% p.a.)`;

  function saveValuesToSession(
    loanAmount,
    repaymentYears,
    interestRate,
    isVariable,
    repaymentFreq,
    isSecured
  ) {
    const data = {
      loanAmount: loanAmount,
      repaymentYears: repaymentYears,
      interestRate: interestRate,
      isVariable: isVariable,
      repaymentFreq: repaymentFreq,
      isSecured: isSecured
    };
    const jsonText = JSON.stringify(data);
    sessionStorage.setItem(SESSION_STORAGE_KEY, jsonText);
  }

  function fetchValuesFromSession() {
    try {
      const jsonText = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (jsonText) {
        const data = JSON.parse(jsonText);
        if (data) {
          return data;
        }
      }
    } catch (err) {
      // Do nothing; fall through...
    }
    return null;
  }

  function onChangeRepaymentFreq(event) {
    const newFreq = event.target.value;
    setRepaymentFreq(newFreq);

    saveValuesToSession(
      loanAmount,
      repaymentYears,
      interestRate,
      isVariable,
      newFreq,
      isSecured
    );

    // trigger analytics call
    !salesMode &&
      directTrackLink(
        true,
        'o',
        getCustomLinkEventName(
          customLinkNames.PERIOD_CHANGE,
          normaliseForAnalytics(newFreq)
        )
      );
  }

  function onChangeRepaymentType(event) {
    const secured = event.target.value === 'true';
    const unsecuredMinInterestRate = getRateBounds({
      loans1,
      isVariable: false,
      isFinder,
      isLefWaiver
    }).minInterestRate;
    const securedMaxInterestRate = getRateBounds({
      loans1,
      isVariable: true,
      isFinder,
      isLefWaiver
    }).maxInterestRate;
    console.log(
      'secured',
      secured,
      interestRate,
      unsecuredMinInterestRate,
      securedMaxInterestRate
    );
    setIsVariable(secured);

    saveValuesToSession(
      loanAmount,
      repaymentYears,
      interestRate,
      secured,
      repaymentFreq,
      isSecured
    );
    // trigger analytics call
    const eventValue = secured ? 'secured' : 'unsecured';
    !salesMode &&
      directTrackLink(
        true,
        'o',
        getCustomLinkEventName(customLinkNames.RATE_TYPE_CHANGE, eventValue)
      );
  }

  function onChangeRepaymentSecurity(event) {
    const secured = event.target.value === 'true';
    setIsSecured(secured);
    saveValuesToSession(
      loanAmount,
      repaymentYears,
      interestRate,
      isVariable,
      repaymentFreq,
      secured
    );
    // trigger analytics call
    const eventValue = secured ? 'secured' : 'unsecured';
    !salesMode &&
      directTrackLink(
        true,
        'o',
        getCustomLinkEventName(customLinkNames.SECURED_CHANGE, eventValue)
      );
  }

  function onChangeRepaymentTerm(value) {
    sessionStorage.setItem('repaymentPeriod', value);
    setRepaymentYears(value);

    // trigger analytics call
    !salesMode &&
      directTrackLink(
        true,
        'o',
        getCustomLinkEventName(customLinkNames.TERM_CHANGE, value)
      );
  }

  function setTimeSavedValues(timeSaved) {
    // average amount of weeks in a month
    const weekToMonthModifier = 4.35;

    // calculate total time saved in months based on repayment frequency
    let timeSavedInMonths = timeSaved;
    if (repaymentFreq === LOAN_PAY_FREQUENCY.FORTNIGHTLY) {
      timeSavedInMonths = (timeSaved / weekToMonthModifier) * 2;
    } else if (repaymentFreq === LOAN_PAY_FREQUENCY.WEEKLY) {
      timeSavedInMonths = timeSaved / weekToMonthModifier;
    }

    // update time saved years and months to absolute rounded value
    timeSavedInMonths = Math.abs(Math.round(timeSavedInMonths));

    setTimeSavedYears(Math.floor(timeSavedInMonths / 12));
    setTimeSavedMonths(timeSavedInMonths % 12);
  }

  const elementRef = useRef(null);

  useEffect(() => {
    const data = fetchValuesFromSession();
    const element = elementRef.current;

    if (data) {
      setLoanAmount(data.loanAmount);
      setRepaymentYears(data.repaymentYears);
      setInterestRate(data.interestRate);
      setIsVariable(data.isVariable);
      setRepaymentFreq(data.repaymentFreq);
      setIsSecured(data.isSecured);
    }

    if (element) {
      const mcpDataLoanAmount = element.dataset.mcploanamount;
      const mcpDataLoanTerm = element.dataset.mcploanterm;
      const mcpDataLoanInterest = element.dataset.mcploaninterest;
      console.log('mcpDataLoanAmount: ' + mcpDataLoanAmount);
      console.log('mcpDataLoanTerm: ' + mcpDataLoanTerm);
      console.log('mcpDataLoanInterest: ' + mcpDataLoanInterest);
      if (mcpDataLoanAmount) {
        setLoanAmount(mcpDataLoanAmount);
      }
      if (mcpDataLoanTerm) {
        setRepaymentYears(mcpDataLoanTerm);
      }
      if (mcpDataLoanInterest) {
        setInterestRate(mcpDataLoanInterest);
      }
    }
  }, []);

  useEffect(() => {
    // stop calculation ONLY when we have invalid input or error
    // should still calculate when having validation warning
    if (loanAmount && loanAmount !== 0 && !hasError) {
      const principle = loanAmount + PL_ESTABLISHMENT_FEE;
      const roundedInterestRate =
        interestRate !== '' &&
        interestRate !== null &&
        interestRate !== undefined
          ? parseFloat(Number(interestRate).toFixed(2))
          : 0;

      const {
        repayment,
        totalInterest,
        totalAmountPaid,
        timeSavedByExtra,
        interestSavedByExtra
      } = getRepaymentFigures(
        principle,
        roundedInterestRate,
        repaymentYears,
        repaymentFreq,
        PL_MONTHLY_FEE,
        extraRepayments,
        true
      );

      setRepaymentAmount(repayment);
      setTotalInterestPaid(totalInterest);
      setTotalAmountPaid(totalAmountPaid);
      setInterestSaved(interestSavedByExtra);
      setTimeSavedValues(timeSavedByExtra);
    }
  }, [
    loanAmount,
    isVariable,
    isSecured,
    repaymentYears,
    repaymentFreq,
    interestRate,
    extraRepayments
  ]);

  const ValidationWatcher = ({
    errors,
    setValidationError,
    validateField,
    isSecured
  }) => {
    useEffect(() => {
      setValidationError(Object.keys(errors).length > 0);
    }, [errors, setValidationError]);

    useEffect(() => {
      validateField('rate');
    }, [isSecured, validateField]);

    return null;
  };

  const initialValues = useMemo(() => {
    const data = fetchValuesFromSession();
    return {
      amount: data?.loanAmount ? Number(data.loanAmount) : loanAmount,
      term: data?.repaymentYears ? Number(data.repaymentYears) : repaymentYears,
      rate: data?.interestRate ? Number(data.interestRate) : interestRate
    };
  }, [loanAmount, repaymentYears, interestRate]);

  return (
    <StyledInputsBox className="lfs-calculator-fields" ref={elementRef}>
      <div
        className="row"
        css={`
          padding: 0px 64px 0px 0px;
          @media (max-width: ${BREAKPOINT.LG}) {
            padding: 0px;
          }
        `}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          validateOnMount
          validateOnChange
          validateOnBlur
          onSubmit={values => {
            console.log(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            validateField
          }) => (
            <Form
              css={`
                width: 100%;
              `}
            >
              <ValidationWatcher
                errors={errors}
                setValidationError={setValidationError}
                validateField={validateField}
                isSecured={isSecured}
              />
              <div
                css={`
                  display: flex;
                  flex-direction: column;
                  gap: 32px;
                `}
              >
                {/* Amount */}
                <CalculatorField className="col-md-12 pb-0 pt-0">
                  <div className="row align-items-start">
                    <div className="col-6">
                      <Typography.CTARegular>
                        I'd like to borrow
                      </Typography.CTARegular>

                      <Typography.Body small color={REBRAND_COLOR.BLACK_80}>
                        ${PL_MINIMUM_AMOUNT.toLocaleString()} - $
                        {PL_MAXIMUM_AMOUNT.toLocaleString()}
                      </Typography.Body>
                    </div>

                    <div className="col-6">
                      <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                        <StyledInput
                          id="calculator-amount"
                          hasError={!!errors.amount}
                          value={`$${Number(values.amount || 0).toLocaleString(
                            'en-GB'
                          )}`}
                          onChange={e => {
                            const value = e.target.value.replace(/[$,]/g, '');

                            setFieldTouched('amount', true);

                            if (/^\d*$/.test(value)) {
                              setFieldValue('amount', Number(value), true);
                              setLoanAmount(Number(value));

                              saveValuesToSession(
                                Number(value),
                                repaymentYears,
                                interestRate,
                                isVariable,
                                repaymentFreq,
                                isSecured
                              );
                            }
                          }}
                          onBlur={e => {
                            const value = e.target.value.replace(/[$,]/g, '');

                            trackEvent?.(analytics, {
                              category: CALCULATOR_TRACKING_V2.CATEGORY,
                              action: CALCULATOR_TRACKING_V2.ACTION,
                              label: 'calculator-amount',
                              location: CALCULATOR_TRACKING_V2.LOCATION,
                              value: value
                            });
                          }}
                        />
                      </Typography.Body>

                      {errors.amount && (
                        <div className="mt-1">
                          <Typography.Body small color={REBRAND_COLOR.RED}>
                            {errors.amount}
                          </Typography.Body>
                        </div>
                      )}
                    </div>
                  </div>
                </CalculatorField>

                {/* Loan Term */}
                <div className="col-md-12 pb-0">
                  <div className="row align-items-start">
                    <div className="col-6">
                      <Typography.CTARegular>Loan Term</Typography.CTARegular>

                      <Typography.Body small color={REBRAND_COLOR.BLACK_80}>
                        {PL_MINIMUM_LOAN_TERM} - {PL_MAXIMUM_LOAN_TERM} years
                      </Typography.Body>
                    </div>

                    <div className="col-6">
                      <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                        <StyledSelect
                          id="calculator-term"
                          isSearchable={false}
                          value={{
                            value: values.term,
                            label: `${values.term} years`
                          }}
                          options={[
                            { value: 2, label: '2 years' },
                            { value: 3, label: '3 years' },
                            { value: 4, label: '4 years' },
                            { value: 5, label: '5 years' },
                            { value: 6, label: '6 years' },
                            { value: 7, label: '7 years' }
                          ]}
                          onChange={option => {
                            setFieldValue('term', Number(option.value));
                            onChangeRepaymentTerm(Number(option.value));
                            saveValuesToSession(
                              loanAmount,
                              Number(option.value),
                              interestRate,
                              isVariable,
                              repaymentFreq,
                              isSecured
                            );
                            trackEvent?.(analytics, {
                              category: CALCULATOR_TRACKING_V2.CATEGORY,
                              action: CALCULATOR_TRACKING_V2.ACTION,
                              label: 'calculator-term',
                              location: CALCULATOR_TRACKING_V2.LOCATION,
                              value: JSON.stringify(option.value)
                            });
                          }}
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: 48,
                              border: `1px solid ${REBRAND_COLOR.BLACK_40}`,
                              borderRadius: 4,
                              boxShadow: 'none',
                              '&:hover': {
                                border: `1px solid ${REBRAND_COLOR.BLACK_40}`
                              }
                            }),
                            valueContainer: provided => ({
                              ...provided,
                              padding: '12px 16px'
                            }),
                            indicatorSeparator: () => ({
                              display: 'none'
                            }),
                            singleValue: provided => ({
                              ...provided,
                              color: REBRAND_COLOR.BLACK_80
                            }),
                            menu: provided => ({
                              ...provided,
                              border: `1px solid ${REBRAND_COLOR.BLACK_40}`,
                              borderTop: 'none',
                              marginTop: '-0.5px',
                              borderTopLeftRadius: 0,
                              borderTopRightRadius: 0
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              padding: '12px 16px',
                              fontFamily: 'inherit',
                              backgroundColor: state.isSelected
                                ? REBRAND_COLOR.BLACK_20
                                : state.isFocused
                                ? REBRAND_COLOR.MID_BLUE_10
                                : 'white',

                              color: REBRAND_COLOR.BLACK_80,
                              cursor: 'pointer',

                              '&:active': {
                                backgroundColor: REBRAND_COLOR.BLACK_20
                              }
                            }),
                            dropdownIndicator: (provided, state) => ({
                              ...provided,
                              color: REBRAND_COLOR.INDIGO,
                              paddingRight: 16,

                              transform: state.selectProps.menuIsOpen
                                ? 'rotateX(180deg)'
                                : 'rotateX(0deg)',

                              transition: 'transform 0.2s ease',

                              '&:hover': {
                                color: REBRAND_COLOR.INDIGO
                              }
                            })
                          }}
                        />
                      </Typography.Body>
                    </div>
                  </div>
                </div>

                {/* Interest Rate */}
                <CalculatorField className="col-md-12 pb-0 pt-0">
                  <div className="row align-items-start">
                    <div className="col-6">
                      <Typography.CTARegular>
                        Interest rate
                      </Typography.CTARegular>
                    </div>

                    <div className="col-6">
                      <Typography.Body color={REBRAND_COLOR.BLACK_80}>
                        <StyledInput
                          id="calculator-interest"
                          hasError={!!errors.rate}
                          value={
                            rateFocused
                              ? values.rate
                              : `${
                                  values.rate !== '' &&
                                  values.rate !== undefined
                                    ? Number(values.rate).toFixed(2)
                                    : ''
                                }% p.a.`
                          }
                          onFocus={() => setRateFocused(true)}
                          onBlur={e => {
                            const value = e.target.value.replace(/[$,]/g, '');

                            trackEvent?.(analytics, {
                              category: CALCULATOR_TRACKING_V2.CATEGORY,
                              action: CALCULATOR_TRACKING_V2.ACTION,
                              label: 'calculator-interest',
                              location: CALCULATOR_TRACKING_V2.LOCATION,
                              value: value
                            });
                            setRateFocused(false);
                          }}
                          onChange={e => {
                            const value = e.target.value
                              .replace('% p.a.', '')
                              .trim();

                            setFieldTouched('rate', true);

                            // allow 1, 1., 1.5, empty
                            if (/^\d*\.?\d*$/.test(value)) {
                              setFieldValue('rate', value, true);

                              setInterestRate(value);

                              saveValuesToSession(
                                loanAmount,
                                repaymentYears,
                                value === '' ? '' : Number(value),
                                isVariable,
                                repaymentFreq,
                                isSecured
                              );
                            }
                          }}
                        />
                      </Typography.Body>

                      {errors.rate && (
                        <div className="mt-1">
                          <Typography.Body small color={REBRAND_COLOR.RED}>
                            {isVariable ? rateVariable : rateFixed}
                          </Typography.Body>
                        </div>
                      )}
                    </div>
                  </div>
                </CalculatorField>
              </div>
            </Form>
          )}
        </Formik>

        <CalculatorField className="col-12 pt-0 pb-4 mb-2">
          <div css={{ marginTop: '10px' }}>
            <div className="row">
              <div className="col-3" css={{ whiteSpace: 'nowrap' }}>
                <Typography.Body small color={REBRAND_COLOR.BLACK_70}>
                  Interest rate
                </Typography.Body>
              </div>
              <div className="col-9" css={{ textAlign: 'right' }}>
                <Typography.Body small color={REBRAND_COLOR.BLACK_70}>
                  From{' '}
                  {isSecured ? SECURED_MIN_RATE : minInterestRate.toFixed(2)}%
                  to {isSecured ? SECURED_MAX_RATE : maxInterestRate.toFixed(2)}
                  % p.a.^
                </Typography.Body>
              </div>
            </div>
            <div className="row">
              <div className="col-3" css={{ whiteSpace: 'nowrap' }}>
                <Typography.Body small color={REBRAND_COLOR.BLACK_70}>
                  Comparison rate
                </Typography.Body>
              </div>
              <div className="col-9">
                <Typography.Body
                  small
                  color={REBRAND_COLOR.BLACK_70}
                  align={ALIGN.RIGHT}
                >
                  From{' '}
                  {isSecured
                    ? SECURED_MIN_COMP_RATE
                    : minComparisonRate.toFixed(2)}
                  % to{' '}
                  {isSecured
                    ? SECURED_MAX_COMP_RATE
                    : maxComparisonRate.toFixed(2)}
                  % p.a.*
                </Typography.Body>
              </div>
            </div>
          </div>
        </CalculatorField>
        <CalculatorField className="col-12 col-xl-6 pt-0 pb-4 mb-2 pb-md-3 mb-md-0">
          <div className="d-flex justify-content-start">
            <Text
              css={`
                white-space: nowrap;
              `}
            >
              <StyledSvg
                name="icon-info-rebrand"
                css={`
                  margin-right: 5px;
                  && path {
                    fill: ${REBRAND_COLOR.INDIGO};
                  }
                `}
              />
              <Typography.LinkSmall
                href="#"
                onClick={e => {
                  setShowModalSecured(true);
                  e.preventDefault();
                }}
                trackEventData={{
                  category: CALCULATOR_TRACKING_V2.CATEGORY,
                  action: CALCULATOR_TRACKING_V2.ACTION,
                  location: CALCULATOR_TRACKING_V2.LOCATION,
                  label: 'Secured or Unsecured?'
                }}
                css={`
                  font-weight: 400;
                  color: ${REBRAND_COLOR.INDIGO};
                  border-color: ${REBRAND_COLOR.INDIGO};
                `}
              >
                Secured or Unsecured?
              </Typography.LinkSmall>
            </Text>
          </div>
          <StyledRadioBoxes
            calculatorTracking={CALCULATOR_TRACKING_V2}
            name="loanSecuredTypeV2"
            onChange={onChangeRepaymentSecurity}
            values={[
              { value: true, text: 'Secured' },
              { value: false, text: 'Unsecured' }
            ]}
            selectedValue={isSecured}
          />
          <Modal
            isOpen={showModalSecured}
            onRequestClose={() => setShowModalSecured(false)}
            width="fit-content"
            maxWidth="576px"
            title={
              <Typography.SH4 color={REBRAND_COLOR.BLACK} semibold>
                Secured vs Unsecured loan?
              </Typography.SH4>
            }
            content={
              <>
                When shopping around or applying for a personal loan, one
                decision you'll need to make is whether you want a secured or
                unsecured loan. Taking out a secured loan means you're able to
                provide an asset like your car, for the lender to secure your
                loan against. An advantage of taking out a secured loan is that
                you may be eligible for a lower interest rate. If you don't have
                a suitable asset to secure your loan against, you may decide to
                apply for an unsecured loan.
                <Button
                  variant="primary"
                  onClick={() => setShowModalSecured(false)}
                  size="lg"
                  ctaText="Close"
                  targetBlank="_blank"
                  style={{ width: '100%', marginTop: '16px' }}
                  trackEventData={{
                    category: CALCULATOR_TRACKING_V2.CATEGORY,
                    action: CALCULATOR_TRACKING_V2.ACTION,
                    location: CALCULATOR_TRACKING_V2.LOCATION,
                    label: 'close modal secured vs unsecured'
                  }}
                />
              </>
            }
            ariaHideApp={false}
          />
        </CalculatorField>
        <CalculatorField className="col-12 col-xl-6 pt-0 pb-4 mb-2 pb-md-3 mb-md-0">
          <div className="d-flex justify-content-start">
            <Text
              css={`
                white-space: nowrap;
              `}
            >
              <StyledSvg
                name="icon-info-rebrand"
                css={`
                  margin-right: 5px;
                  && path {
                    fill: ${REBRAND_COLOR.INDIGO};
                  }
                `}
              />
              <Typography.LinkSmall
                href="#"
                onClick={e => {
                  setShowModal(true);
                  e.preventDefault();
                }}
                trackEventData={{
                  category: CALCULATOR_TRACKING_V2.CATEGORY,
                  action: CALCULATOR_TRACKING_V2.ACTION,
                  location: CALCULATOR_TRACKING_V2.LOCATION,
                  label: 'Fixed or Variable?'
                }}
                css={`
                  font-weight: 400;
                  color: ${REBRAND_COLOR.INDIGO};
                  border-color: ${REBRAND_COLOR.INDIGO};
                `}
              >
                Fixed or Variable?
              </Typography.LinkSmall>
            </Text>
          </div>
          <StyledRadioBoxes
            calculatorTracking={CALCULATOR_TRACKING_V2}
            name="repaymentsTypeV2"
            onChange={onChangeRepaymentType}
            values={[
              { value: false, text: 'Fixed' },
              { value: true, text: 'Variable' }
            ]}
            selectedValue={isVariable}
          />
          <Modal
            isOpen={showModal}
            onRequestClose={() => setShowModal(false)}
            width="fit-content"
            maxWidth="576px"
            title={
              <Typography.SH4 color={REBRAND_COLOR.BLACK} semibold>
                Fixed vs Variable loan?
              </Typography.SH4>
            }
            content={
              <>
                When applying for a personal loan, one decision you need is to
                make is whether you want a fixed or variable loan.
                <br />
                <br />
                <strong>Fixed loan:</strong>
                <List>
                  <ListItem>
                    Your rate remains fixed for the life of your loan
                  </ListItem>
                  <ListItem>
                    An early termination fee of $500 applies if you payout your
                    loan in full up until the last 3 months of the loan. No
                    early termination fee applies in the final 3 months of the
                    loan
                  </ListItem>
                </List>
                <br />
                <strong>Variable loan:</strong>
                <List>
                  <ListItem>
                    Your rate could change, up or down, during your loan term
                  </ListItem>
                  <ListItem>No early termination fee</ListItem>
                </List>
                <Button
                  variant="primary"
                  onClick={() => setShowModal(false)}
                  size="lg"
                  ctaText="Close"
                  targetBlank="_blank"
                  style={{ width: '100%', marginTop: '16px' }}
                  trackEventData={{
                    category: CALCULATOR_TRACKING_V2.CATEGORY,
                    action: CALCULATOR_TRACKING_V2.ACTION,
                    location: CALCULATOR_TRACKING_V2.LOCATION,
                    label: 'close modal fixed vs variable'
                  }}
                />
              </>
            }
            ariaHideApp={false}
          />
        </CalculatorField>
        <CalculatorField className="col-12 pt-0 pb-0">
          <StyledRadioBoxes
            calculatorTracking={CALCULATOR_TRACKING_V2}
            labelText={
              <Typography.CTARegular>
                Make repayments every
              </Typography.CTARegular>
            }
            name="repaymentFreqV2"
            onChange={onChangeRepaymentFreq}
            values={[
              { text: 'Week', value: LOAN_PAY_FREQUENCY.WEEKLY },
              {
                text: 'Fortnight',
                value: LOAN_PAY_FREQUENCY.FORTNIGHTLY
              },
              { text: 'Month', value: LOAN_PAY_FREQUENCY.MONTHLY }
            ]}
            selectedValue={repaymentFreq}
            equalWidths
          />
        </CalculatorField>
      </div>
    </StyledInputsBox>
  );
}

const StyledInputsBox = styled(Box)`
  width: 100%;

  @media (min-width: ${BREAKPOINT.LG}) {
    width: 50%;
  }
  && .typography-base__body--small {
    font-weight: 500;
  }
`;

const StyledRadioBoxes = styled(RadioBoxes)`
  border: 1px solid ${REBRAND_COLOR.INDIGO};
  border-radius: 4px;
  padding: 5px;
  margin-top: 12px;
  && label.lfs-radio-label {
    border: none;
    border-radius: 4px;
  }
  && .radio-box__text,
  && .radio-box__input:checked + .radio-box__text {
    color: ${REBRAND_COLOR.INDIGO} !important;
  }
  && .radio-box__label.active,
  && .radio-box__input:checked + .radio-box__text {
    background-color: ${REBRAND_COLOR.GREEN} !important;
  }
  && .radio-box__label.active span {
    color: ${REBRAND_COLOR.INDIGO} !important;
  }
  && label.lfs-radio-label.active {
    background-color: 006aff;
  }
  && .input-wrap label {
    margin-bottom: 0px;
  }
`;

const CalculatorField = styled.div`
  padding: 15px;
`;

const StyledSelect = styled(Select)``;

const StyledInput = styled.input`
  font: inherit;
  width: 100%;
  height: 48px;
  border: 1px solid ${REBRAND_COLOR.BLACK_40};
  border-radius: 4px;
  padding: 12px 16px;
  box-sizing: border-box;
  color: ${REBRAND_COLOR.BLACK_80};

  border-bottom: ${({ hasError }) =>
    hasError
      ? `2px solid ${REBRAND_COLOR.RED}`
      : `1px solid ${REBRAND_COLOR.BLACK_40}`};

  ${({ hasError }) =>
    hasError &&
    `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}

  &:focus {
    outline: none;
    border-bottom: 2px solid
      ${({ hasError }) => (hasError ? REBRAND_COLOR.RED : REBRAND_COLOR.INDIGO)};

    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const StyledSvg = styled(SvgInline)`
  width: 20px;
  height: 20px;
  position: relative;
  top: 5px;
  margin-left: 5px;
`;

export default CalculatorFields;
